import React, { useState } from 'react';

/**
 * ShipmentBadge
 *
 * Reusable badge for displaying shipment status in admin tables and detail views.
 * Maps the async backend statuses (from order.shiprocket.status) to human-readable
 * labels and colour-coded badges.
 *
 * IMPORTANT — async system awareness:
 * Shipment is NOT created instantly after payment. Backend uses BullMQ async queue.
 * `order.shiprocket` may be undefined for a short time after order creation.
 * Always render "Not Started" gracefully when shiprocket object is absent.
 *
 * @param {string} status - order.shiprocket.status value (or undefined)
 * @param {boolean} showTooltip - whether to show the explanation tooltip on hover
 * @param {string} className - extra classes
 */
export default function ShipmentBadge({ status, showTooltip = true, className = '' }) {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const config = getStatusConfig(status);

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={() => showTooltip && setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
    >
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-sans font-medium whitespace-nowrap ${config.classes}`}
      >
        <span>{config.dot}</span>
        {config.label}
      </span>

      {/* Tooltip — explains the async state to admin */}
      {showTooltip && tooltipVisible && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-52 pointer-events-none">
          <div className="bg-headingText text-primaryBg text-xs font-sans rounded-lg px-3 py-2 shadow-lg text-center leading-relaxed">
            {config.tooltip}
          </div>
          {/* Arrow */}
          <div className="flex justify-center">
            <div className="w-2 h-2 bg-headingText rotate-45 -mt-1" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Get badge config for a given shiprocket.status value.
 * Covers all states the async worker can produce.
 */
export function getStatusConfig(status) {
  switch (status) {
    case 'processing':
      return {
        label: 'Processing',
        dot: '⏳',
        classes: 'bg-yellow-100 text-yellow-800',
        tooltip: 'In queue — BullMQ worker is creating the shipment. May take up to 30s.',
      };
    case 'failed_reconciling':
      return {
        label: 'Retrying',
        dot: '🔄',
        classes: 'bg-orange-100 text-orange-800',
        tooltip: 'Auto-recovery in progress — reconciliation worker is retrying after a soft failure.',
      };
    case 'permanent_failed':
      return {
        label: 'Failed',
        dot: '🆘',
        classes: 'bg-red-100 text-red-800',
        tooltip: 'All automatic retries exhausted. Manual action required — use the Retry button.',
      };
    case 'completed':
      return {
        label: 'Shipped',
        dot: '✅',
        classes: 'bg-green-100 text-green-800',
        tooltip: 'Shipment created successfully. AWB and courier assigned.',
      };
    case 'failed':
      return {
        label: 'Failed (soft)',
        dot: '⚠️',
        classes: 'bg-orange-100 text-orange-800',
        tooltip: 'Shipment creation failed — auto-retry will run shortly. Monitor for escalation to permanent_failed.',
      };
    default:
      // No shiprocket object or unknown status
      return {
        label: 'Not Started',
        dot: '○',
        classes: 'bg-surface text-textSecondary border border-secondary/20',
        tooltip: 'Shipment has not been queued yet, or order predates the async pipeline.',
      };
  }
}
